import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Download, RotateCcw, Send, Check } from "lucide-react";
import { LOGO_URL } from "@/constants/brand";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LEVELS = [
  { id: "platinum", label: "Platinum", amount: 1000000 },
  { id: "gold", label: "Gold", amount: 500000 },
  { id: "silver", label: "Silver", amount: 200000 },
  { id: "bronze", label: "Bronze", amount: 100000 },
  { id: "other", label: "Other", amount: null },
];

const PAYMENTS = ["Cash", "Check", "Bank Transfer"];

const fmtPeso = (n) => {
  if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "";
  return new Intl.NumberFormat("en-PH", { minimumFractionDigits: 2 }).format(Number(n));
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric" });
};

const blankForm = {
  no: "00001",
  date: todayIso(),
  name: "",
  address: "",
  contact: "",
  email: "",
  level: "",
  otherAmount: "",
  payment: "",
  receivedBy: "",
  officeDate: "",
  signature: "",
};

export default function DonorForm() {
  const [form, setForm] = useState(blankForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Donor Pledge Form · Saint Francis of Assisi Parish";
  }, []);

  const update = (k) => (e) => {
    setSent(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };
  const reset = () => {
    setForm({ ...blankForm, date: todayIso() });
    setSent(false);
  };

  // Compute effective amount based on level
  const effectiveAmount = useMemo(() => {
    if (!form.level) return null;
    if (form.level === "other") {
      const n = Number(form.otherAmount);
      return isNaN(n) || n <= 0 ? null : n;
    }
    return LEVELS.find((l) => l.id === form.level)?.amount ?? null;
  }, [form.level, form.otherAmount]);

  const levelLabel = useMemo(() => {
    const l = LEVELS.find((x) => x.id === form.level);
    return l ? l.label : "";
  }, [form.level]);

  const sendToParish = async () => {
    if (!form.name.trim()) {
      toast.error("Please enter your name before sending.");
      return;
    }
    if (!form.level) {
      toast.error("Please choose a donation level.");
      return;
    }
    setSending(true);
    try {
      const payload = {
        no: form.no || null,
        date: form.date || null,
        name: form.name,
        address: form.address,
        contact: form.contact,
        email: form.email || null,
        level: form.level,
        level_label: levelLabel,
        amount: effectiveAmount,
        payment: form.payment,
      };
      await api.post("/pledges", payload);
      setSent(true);
      toast.success("Pledge sent — pax et bonum. The parish office will be in touch.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not send. Please try again or call the parish office.";
      toast.error(typeof msg === "string" ? msg : "Could not send pledge.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-parchment-100 min-h-screen">
      <style>{`
        @page { size: A4 portrait; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body, html { background: white !important; }
          .pledge-sheet { box-shadow: none !important; border: none !important; padding: 0 !important; }
          .tear-line { border-top: 2px dashed #5C4033 !important; }
          input, .fillable {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            color: #3E2723 !important;
            background: transparent !important;
          }
          /* Hide unselected radio fakes on print, keep selected ones */
          .ck-fake[data-checked="false"] { background: transparent !important; }
          .ck-fake[data-checked="true"]::before {
            content: "✓";
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            color: #3E2723;
          }
        }
        .field-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid #7A5C4F;
          padding: 4px 2px 6px;
          font-family: 'EB Garamond', Georgia, serif;
          font-size: 1.05rem;
          color: #3E2723;
          outline: none;
          transition: border-color 0.2s;
        }
        .field-input:focus {
          border-bottom-color: #C25953;
        }
        .field-input::placeholder {
          color: rgba(62,39,35,0.25);
          font-style: italic;
        }
      `}</style>

      {/* Top action bar */}
      <div className="no-print sticky top-0 z-40 bg-parchment-100/95 backdrop-blur border-b border-parchment-400">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-sans text-cocoa-800 hover:text-terracotta transition-colors"
            data-testid="donor-back-home"
          >
            <ArrowLeft size={16} /> Back to parish website
          </Link>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={sendToParish}
              disabled={sending}
              data-testid="donor-send-button"
              className="inline-flex items-center gap-2 bg-terracotta text-parchment-100 px-5 py-2.5 text-sm font-sans hover:bg-terracotta-dark transition-colors disabled:opacity-60"
            >
              {sent ? <Check size={16} /> : <Send size={16} />}
              {sending ? "Sending…" : sent ? "Sent ✓" : "Send to Parish"}
            </button>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-2 text-sm font-sans text-cocoa-700 hover:text-terracotta transition-colors px-3"
              data-testid="donor-reset-button"
            >
              <RotateCcw size={15} /> Reset
            </button>
            <button
              data-testid="donor-print-button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 bg-cocoa-900 text-parchment-100 px-5 py-2.5 text-sm font-sans hover:bg-terracotta transition-colors"
            >
              <Printer size={16} /> Print
            </button>
            <button
              data-testid="donor-download-button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 border border-cocoa-900/40 text-cocoa-900 px-5 py-2.5 text-sm font-sans hover:bg-cocoa-900 hover:text-parchment-100 transition-colors"
              title="Use your browser's print dialog and choose 'Save as PDF'"
            >
              <Download size={16} /> Save as PDF
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-6 pb-3 text-[11px] uppercase tracking-[0.22em] text-cocoa-700 font-sans flex flex-wrap items-center gap-3">
          <span>Tip · Fill the form, then <em>Send to Parish</em> for a digital copy, or <em>Save as PDF</em>.</span>
          {sent && (
            <span
              data-testid="donor-sent-confirmation"
              className="inline-flex items-center gap-2 bg-sage/20 text-sage-dark px-3 py-1 normal-case tracking-normal"
            >
              <Check size={14} /> Your pledge has been received by the parish office.
            </span>
          )}
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-10 print:py-0">
        <article className="pledge-sheet bg-parchment-50 border border-parchment-400 shadow-xl p-8 md:p-12 font-sans text-cocoa-900">
          {/* Header with logo */}
          <header className="flex items-center justify-between gap-6 pb-6 border-b-2 border-cocoa-900">
            <img
              src={LOGO_URL}
              alt="Saint Francis of Assisi Parish seal"
              className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border border-parchment-400 bg-parchment-100"
            />
            <div className="text-center flex-1">
              <div className="text-[10px] uppercase tracking-[0.32em] text-terracotta">
                Pledge & Donation Form
              </div>
              <h1 className="font-serif text-2xl md:text-3xl text-cocoa-900 mt-1 leading-tight">
                Saint Francis of Assisi Parish
              </h1>
              <div className="text-[11px] uppercase tracking-[0.22em] text-cocoa-700 mt-1">
                Brgy. Mabuhay · General Santos City · Diocese of Marbel
              </div>
              <div className="mt-3 inline-block text-[10px] uppercase tracking-[0.22em] text-sage-dark border border-sage/40 px-3 py-1">
                Commemorating the 800th Anniversary of the Transit of St. Francis · 1226 — 2026
              </div>
            </div>
            <div className="w-20 md:w-24 hidden md:block" />
          </header>

          {/* Pledge statement + No + Date */}
          <div className="mt-6 grid grid-cols-12 gap-4 items-end">
            <div className="col-span-12 md:col-span-8">
              <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark">
                I / We hereby pledge to support
              </div>
              <p className="font-serif italic text-2xl text-terracotta leading-tight mt-1">
                The building of Saint Francis of Assisi Parish.
              </p>
            </div>
            <div className="col-span-6 md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] text-cocoa-700 mb-1 block">No.</label>
              <input
                className="field-input font-serif text-terracotta text-xl"
                value={form.no}
                onChange={update("no")}
                data-testid="donor-no"
              />
            </div>
            <div className="col-span-6 md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] text-cocoa-700 mb-1 block">Date</label>
              <input
                type="date"
                className="field-input text-sm"
                value={form.date}
                onChange={update("date")}
                data-testid="donor-date"
              />
            </div>
          </div>

          {/* Donor Information */}
          <section className="mt-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-terracotta font-medium mb-5">
              Donor Information
            </div>
            <div className="grid grid-cols-12 gap-x-6 gap-y-6">
              <div className="col-span-12">
                <Label>Name / Company</Label>
                <input
                  className="field-input"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name or organization"
                  data-testid="donor-name"
                />
              </div>
              <div className="col-span-12">
                <Label>Address</Label>
                <input
                  className="field-input"
                  value={form.address}
                  onChange={update("address")}
                  placeholder="House No., Street, Barangay, City"
                  data-testid="donor-address"
                />
              </div>
              <div className="col-span-12 md:col-span-7">
                <Label>Contact No.</Label>
                <input
                  className="field-input"
                  value={form.contact}
                  onChange={update("contact")}
                  placeholder="09XX XXX XXXX"
                  data-testid="donor-contact"
                />
              </div>
              <div className="col-span-12 md:col-span-5">
                <Label>Email</Label>
                <input
                  type="email"
                  className="field-input"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="name@example.com"
                  data-testid="donor-email"
                />
              </div>
            </div>
          </section>

          {/* Ways to Give */}
          <section className="mt-10">
            <div className="text-[11px] uppercase tracking-[0.28em] text-terracotta font-medium mb-4">
              Ways to Give &nbsp;·&nbsp;
              <span className="text-cocoa-700 normal-case tracking-normal text-[11px]">Choose one level</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {LEVELS.map((l) => {
                const checked = form.level === l.id;
                return (
                  <label
                    key={l.id}
                    className={`flex items-start gap-3 border px-3 py-3 cursor-pointer transition-colors ${
                      checked
                        ? "border-terracotta bg-terracotta/10"
                        : "border-cocoa-900/30 bg-parchment-100 hover:border-cocoa-900/60"
                    }`}
                  >
                    <input
                      type="radio"
                      name="level"
                      value={l.id}
                      checked={checked}
                      onChange={update("level")}
                      className="sr-only"
                      data-testid={`donor-level-${l.id}`}
                    />
                    <span
                      className="ck-fake relative inline-block w-4 h-4 border-2 border-cocoa-900 mt-0.5 flex-shrink-0"
                      data-checked={checked ? "true" : "false"}
                    >
                      {checked && (
                        <span className="absolute inset-0 flex items-center justify-center text-cocoa-900 text-[12px] leading-none">
                          ✓
                        </span>
                      )}
                    </span>
                    <span className="flex-1">
                      <span className="block font-serif text-cocoa-900 text-base leading-tight">
                        {l.label}
                      </span>
                      {l.amount !== null ? (
                        <span className="block text-[11px] text-terracotta tracking-wide mt-1">
                          ₱ {fmtPeso(l.amount)}
                        </span>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.otherAmount}
                          onChange={update("otherAmount")}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={() => setForm((f) => ({ ...f, level: "other" }))}
                          placeholder="₱ amount"
                          className="mt-1 w-full bg-transparent border-0 border-b border-cocoa-900/40 focus:border-terracotta py-0.5 text-[11px] tracking-wide text-terracotta focus:outline-none"
                          data-testid="donor-other-amount"
                        />
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </section>

          {/* Payment + office use */}
          <section className="mt-10 grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.28em] text-terracotta font-medium mb-4">
                Payment Method
              </div>
              <div className="space-y-3 text-sm">
                {PAYMENTS.map((p) => {
                  const checked = form.payment === p;
                  return (
                    <label key={p} className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="payment"
                        value={p}
                        checked={checked}
                        onChange={update("payment")}
                        className="sr-only"
                        data-testid={`donor-payment-${p.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                      <span
                        className="ck-fake relative inline-block w-4 h-4 border-2 border-cocoa-900 mt-0.5 flex-shrink-0"
                        data-checked={checked ? "true" : "false"}
                      >
                        {checked && (
                          <span className="absolute inset-0 flex items-center justify-center text-cocoa-900 text-[12px] leading-none">
                            ✓
                          </span>
                        )}
                      </span>
                      <span>
                        <span className="font-medium">{p}</span>
                        {p === "Check" && (
                          <span className="block text-[11px] text-cocoa-700 italic mt-0.5">
                            Please make check payable to St. Francis of Assisi Parish.
                          </span>
                        )}
                        {p === "Bank Transfer" && (
                          <span className="block text-[11px] text-cocoa-700 mt-0.5 leading-snug">
                            BPI – General Santos Heights · 007303-0062-86
                            <br />
                            The Roman Catholic Bishop of Marbel, Inc. — St. Francis of Assisi Parish
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="col-span-12 md:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.28em] text-terracotta font-medium mb-4">
                Office Use Only
              </div>
              <div className="grid grid-cols-12 gap-x-6 gap-y-6">
                <div className="col-span-12 md:col-span-8">
                  <Label>Received by</Label>
                  <input
                    className="field-input"
                    value={form.receivedBy}
                    onChange={update("receivedBy")}
                    data-testid="donor-received-by"
                  />
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Label>Date</Label>
                  <input
                    type="date"
                    className="field-input text-sm"
                    value={form.officeDate}
                    onChange={update("officeDate")}
                    data-testid="donor-office-date"
                  />
                </div>
                <div className="col-span-12">
                  <Label>Authorized Signature</Label>
                  <input
                    className="field-input"
                    value={form.signature}
                    onChange={update("signature")}
                    data-testid="donor-signature"
                  />
                </div>
              </div>

              <p className="mt-5 text-[11px] uppercase tracking-[0.22em] text-sage-dark">
                ✦ Collect your Official Receipt. Thank you very much!
              </p>
            </div>
          </section>

          {/* Tear line */}
          <div className="tear-line border-t-2 border-dashed border-cocoa-900/70 mt-12 pt-6 relative">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-parchment-50 px-4 text-[10px] uppercase tracking-[0.28em] text-cocoa-700">
              ✂ Detach · Donor&apos;s Stub
            </span>
          </div>

          {/* Donor's Stub — auto-mirrored values */}
          <section>
            <div className="grid grid-cols-12 gap-4 items-end mb-5">
              <div className="col-span-12 md:col-span-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark">
                  Donor&apos;s Stub
                </div>
                <p className="font-serif italic text-xl text-terracotta leading-tight mt-1">
                  Please keep this portion.
                </p>
              </div>
              <StubField label="No." value={form.no} mono />
              <StubField label="Date" value={fmtDate(form.date)} />
            </div>

            <div className="grid grid-cols-12 gap-x-6 gap-y-5">
              <StubField wide label="Name / Company" value={form.name} className="col-span-12" />
              <StubField label="Donation Level" value={levelLabel} className="col-span-12 md:col-span-6" />
              <StubField
                label="Amount (₱)"
                value={effectiveAmount !== null ? fmtPeso(effectiveAmount) : ""}
                className="col-span-12 md:col-span-6"
              />
              <StubField label="Received by" value={form.receivedBy} className="col-span-12" />
            </div>

            <div className="mt-8 text-center border-t border-parchment-400 pt-6">
              <p className="font-serif italic text-2xl text-cocoa-900">
                Thank you for your generosity.
              </p>
              <p className="text-[11px] uppercase tracking-[0.28em] text-sage-dark mt-2">
                May the Lord bless you abundantly · Pax et Bonum
              </p>
            </div>
          </section>

          {/* Footer with parish contact */}
          <footer className="mt-10 pt-5 border-t border-parchment-400 grid md:grid-cols-3 gap-4 text-[11px] text-cocoa-700">
            <div>
              <div className="uppercase tracking-[0.22em] text-sage-dark mb-1">Parish Office</div>
              0915 581 1419
            </div>
            <div>
              <div className="uppercase tracking-[0.22em] text-sage-dark mb-1">Email</div>
              sfap2028@gmail.com
            </div>
            <div className="md:text-right">
              <div className="uppercase tracking-[0.22em] text-sage-dark mb-1">Website</div>
              mabuhayparish.org
            </div>
          </footer>
        </article>
      </main>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="text-[10px] uppercase tracking-[0.24em] text-cocoa-700 mb-1 block font-sans">
      {children}
    </label>
  );
}

function StubField({ label, value, mono, wide, className = "" }) {
  const colSpan = wide ? "col-span-12" : "col-span-6 md:col-span-2";
  return (
    <div className={className || colSpan}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-cocoa-700 mb-1">{label}</div>
      <div
        className={`fillable min-h-[28px] border-b border-cocoa-700/70 pb-1 ${
          mono ? "font-mono text-base" : "font-serif text-lg leading-snug"
        } text-cocoa-900`}
      >
        {value || "\u00A0"}
      </div>
    </div>
  );
}
